import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useProcedureValidator} from 'studies/typed/shared/ProcedureDropdown';
import {getProcedureRunUIStringForStudyType, getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';

interface ConfirmPromotionModalProps {
  studyType: StudyType;
  procedureId: string;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
}

export default function ConfirmPromotionModal(props: ConfirmPromotionModalProps): JSX.Element {
  const procedureType = getProcedureTypeForStudyType(props.studyType);
  const [isProcedureValid, setIsProcedureValid] = useState(false);
  // Start with null so useProcedureValidator triggers onChangeToValidatedProcedure when validation passes
  const [selectedProcedureId, setSelectedProcedureId] = useState<string | null>(null);

  const procedureValidationResultElement = useProcedureValidator({
    procedureType,
    selectedProcedureId,
    onChangeToValidatedProcedure: (procedureId: string | null) => {
      setSelectedProcedureId(procedureId);
      setIsProcedureValid(procedureId === props.procedureId);
    },
    onRequestCloseModal: props.onRequestClose,
    stagedProcedureForValidationId: props.procedureId,
  });

  return (
    <Modal
      dataTestAttrs={{component: 'ConfirmPromotionModal', element: 'root'}}
      title={`Create ${getProcedureRunUIStringForStudyType(props.studyType)}`}
      buttons={[
        {
          text: 'Create',
          submit: true,
          style: ButtonStyle.PRIMARY,
          disabled: !isProcedureValid,
          dataTestAttrs: {component: 'ConfirmPromotionModal', element: 'createButton'},
        },
        {
          text: 'Cancel',
          submit: false,
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: () => {
            props.onRequestClose();
          },
        },
      ]}
      onRequestSubmit={() => {
        props.onRequestSubmit();
      }}
      onRequestClose={props.onRequestClose}
    >
      <p className={css(styles.modalBody)}>
        This finalizes your {getProcedureRunUIStringForStudyType(props.studyType)} design.
      </p>
      <p className={css(styles.modalBody)}>
        We’ll create unit operation instances for you to execute the conditions you’ve planned. After that,
        condition setpoints can’t be edited. You can still add new conditions and link unit operation
        instances.
      </p>
      {procedureValidationResultElement}
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalBody: {
    fontSize: '14px',
  },
});
